import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { IncognitoChat, NewChat } from "~/components/chat";
import { createLocalChatSession } from "~/lib/chat/local-mutations";
import { toLocalDbError } from "~/lib/db/local-errors";
import { $getPromptTemplate } from "~/lib/queries";

const searchSchema = z.object({
  templateId: z.string().optional(),
});

export const Route = createFileRoute("/(authenticated)/dashboard/chat/new")({
  component: NewChatPage,
  validateSearch: searchSchema,
});

interface IncognitoState {
  message: string;
  model: string;
  providerId: string | null;
  systemPrompt?: string;
  imageUrls?: string[];
}

// Query key for incognito state storage
const INCOGNITO_STATE_KEY = ["incognito-chat-state"] as const;

function NewChatPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { templateId } = Route.useSearch();
  const { user } = Route.useRouteContext();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if we're in incognito mode via URL hash
  const hash = useRouterState({ select: (s) => s.location.hash });
  const isIncognitoMode = hash === "incognito";

  // Clear incognito state from cache when hash is removed (navigating to fresh new chat)
  useEffect(() => {
    if (!isIncognitoMode) {
      queryClient.removeQueries({ queryKey: INCOGNITO_STATE_KEY });
    }
  }, [isIncognitoMode, queryClient]);

  // Get incognito state from query cache when hash is present
  const incognitoState = isIncognitoMode
    ? queryClient.getQueryData<IncognitoState>(INCOGNITO_STATE_KEY)
    : null;

  const { data: template } = useQuery({
    queryKey: ["prompt-template", templateId],
    queryFn: () => $getPromptTemplate({ data: { id: templateId! } }),
    enabled: !!templateId,
  });

  const handleSubmit = async (
    message: string,
    model: string,
    providerId: string | null,
    isIncognito: boolean,
    imageUrls?: string[],
  ) => {
    setIsSubmitting(true);

    try {
      if (isIncognito) {
        // Store incognito state in query cache
        queryClient.setQueryData(INCOGNITO_STATE_KEY, {
          message,
          model,
          providerId,
          systemPrompt: template?.systemPrompt ?? undefined,
          imageUrls,
        });

        // Navigate to same route with #incognito hash
        navigate({
          to: "/dashboard/chat/new",
          search: templateId ? { templateId } : undefined,
          hash: "incognito",
        });
        setIsSubmitting(false);
      } else {
        // Create the chat session locally (privacy-first)
        const session = await createLocalChatSession({
          templateId: templateId || null,
          title: generateChatTitle(message, template?.name),
          model,
          providerId,
        });

        // Don't save the message to local DB yet - let the chat interface handle it
        // This prevents duplicate messages when using pendingMessage
        // Store pending message in query cache for the chat page to pick up
        queryClient.setQueryData(["pending-message", session.id], {
          content: message,
          model,
          providerId,
          systemPrompt: template?.systemPrompt,
          imageUrls,
        });

        // Note: Recent chats will auto-update via Dexie's useLiveQuery
        // No need to invalidate - the sidebar uses reactive local queries

        // Navigate to the chat page
        navigate({
          to: "/dashboard/chat/$chatId",
          params: { chatId: session.id },
        });
      }
    } catch (error) {
      console.error("[NEW CHAT] Failed to create chat:", error);

      const localError = toLocalDbError(error);
      if (localError.code === "quota_exceeded") {
        toast.error("Local storage full", {
          description: localError.message,
          action: {
            label: "Sync settings",
            onClick: () => navigate({ to: "/dashboard/settings/sync" }),
          },
          duration: 10000,
        });
      } else {
        toast.error("Failed to create chat", {
          description: localError.message,
        });
      }

      setIsSubmitting(false);
    }
  };

  // If incognito chat is active, render it instead of the new chat form
  if (incognitoState) {
    return (
      <IncognitoChat
        initialMessage={incognitoState.message}
        model={incognitoState.model}
        providerId={incognitoState.providerId}
        systemPrompt={incognitoState.systemPrompt}
        imageUrls={incognitoState.imageUrls}
        template={template ?? null}
        user={user}
      />
    );
  }

  return (
    <NewChat template={template} onSubmit={handleSubmit} isSubmitting={isSubmitting} />
  );
}

function generateChatTitle(message: string, templateName?: string): string {
  if (templateName) {
    return templateName;
  }
  const truncated = message.slice(0, 50);
  return message.length > 50 ? `${truncated}...` : truncated;
}
